// FNP-1500: check every derived name for availability BEFORE provisioning runs.
//
// ═══ 🛑 FAIL CLOSED — READ BEFORE CHANGING ANYTHING HERE ═══
//
// A listing that fails permissions returns ZERO ITEMS and is indistinguishable
// from an empty account. Every lookup in this file therefore produces a
// `ResourceListing` carrying an explicit `complete` flag, and only a listing
// with `complete: true` may be read as evidence that a name is free.
//
// ⚠️ NOTHING IN THIS FILE MAY THROW PAST ITS OWN LOOKUP. A rejected promise
// that escapes would either fail the whole check (losing the statuses that DID
// resolve) or, worse, be caught upstream and rendered as an empty result. Each
// lookup catches its own failure and reports `complete: false`.
//
// The teardown side makes the same distinction — see
// verify-infrastructure-absent.ts in flexinet-console-api.

import {GraphQLQuery} from '@aws-amplify/api';

import {pipelinesClient} from '../../pipelines-api-integration/client';
import {
    listBuckets,
    listCodePipelines,
    listCodeRepositories,
    listStacks,
} from './queries';
import {
    CheckNameAvailabilityInput,
    DerivedName,
    NameAvailability,
    NameAvailabilityResult,
} from '../types';
import {ListingFailureKind} from '../utils/listingFailureKind';
import {
    deriveNames,
    validateBaseName,
} from '../utils/deriveNames';

const client = pipelinesClient;

// ⚠️ A cap, not a page size. `listCodePipelines` has no filter argument, so the
// only way to know whether a pipeline name is taken is to drain the whole
// listing. If the drain hits this cap the listing is INCOMPLETE and its names
// prove nothing — see the `complete: false` return below.
const MAX_PIPELINE_PAGES = 20;

// One listing's outcome, normalized across the four resource types.
//
// Deliberately structural rather than the raw pipelines-api types: the four
// listings return four different item shapes and this check cares only about
// `complete` and the resource NAMES found.
interface ResourceListing {
    readonly complete: boolean;
    readonly names: string[];
    readonly failureKind?: ListingFailureKind | null;
    readonly failureReason?: string | null;
}

type ListCodeRepositoriesResponse = {
    listCodeRepositories: {
        items: ({ name: string } | null)[] | null;
        nextToken?: string | null;
    } | null;
};

type ListCodePipelinesResponse = {
    listCodePipelines: {
        items: ({ name: string } | null)[] | null;
        nextToken?: string | null;
    } | null;
};

type ListStacksResponse = {
    listStacks: {
        items: ({ stackName: string; stackStatus: string } | null)[] | null;
        complete: boolean;
        failureKind?: ListingFailureKind | null;
        failureReason?: string | null;
    } | null;
};

type ListBucketsResponse = {
    listBuckets: {
        items: ({ name: string } | null)[] | null;
        complete: boolean;
        failureKind?: ListingFailureKind | null;
        failureReason?: string | null;
    } | null;
};

// Turn a caught error into an INCOMPLETE listing.
//
// ⚠️ Never returns `complete: true`. This is the branch that stops a network
// error, an expired session or an authorization failure from being rendered as
// "the name is available".
const failed = (e: unknown, kind: ListingFailureKind = 'ERROR'): ResourceListing => ({
    complete: false,
    names: [],
    failureKind: kind,
    failureReason: e instanceof Error ? e.message : String(e),
});

// An Amplify GraphQL error carries partial data alongside `errors`. Treat any
// error entry as a failed listing: a partial response cannot prove absence.
const graphQLErrorKind = (e: unknown): ListingFailureKind => {
    const message = (e instanceof Error ? e.message : String(e)).toLowerCase();
    if (message.includes('unauthorized')
        || message.includes('not authorized')
        || message.includes('access denied')) {
        return 'ACCESS_DENIED';
    }
    if (message.includes('throttl') || message.includes('rate exceeded')) {
        return 'THROTTLED';
    }
    return 'ERROR';
};

// Repositories, by name prefix.
//
// ⚠️ This query has no `complete` field — it is an ordinary paginated
// connection. A listing that we stop draining early therefore has to be
// reported incomplete by US, which is what the nextToken check below does.
const listRepositoryNames = async (namePrefix: string): Promise<ResourceListing> => {
    try {
        const response = await client.graphql<GraphQLQuery<ListCodeRepositoriesResponse>>({
            query: listCodeRepositories,
            variables: {filter: {namePrefix}, limit: 100, nextToken: null},
        });

        const connection = response.data?.listCodeRepositories;
        if (!connection) {
            return failed(new Error('listCodeRepositories returned no data'));
        }

        const names = (connection.items ?? [])
            .filter((item): item is { name: string } => !!item)
            .map((item) => item.name);

        // A prefix listing that still has a nextToken has NOT been exhausted,
        // so a name we did not see may still exist further down.
        if (connection.nextToken) {
            return {
                complete: false,
                names,
                failureKind: 'TRUNCATED',
                failureReason: 'The repository listing did not reach the end of '
                    + 'its results, so a matching repository may exist beyond '
                    + 'the page that was read.',
            };
        }

        return {complete: true, names};
    } catch (e) {
        return failed(e, graphQLErrorKind(e));
    }
};

// Pipelines. No filter argument exists, so this drains the whole listing.
const listPipelineNames = async (): Promise<ResourceListing> => {
    const names: string[] = [];
    let nextToken: string | null = null;

    try {
        for (let page = 0; page < MAX_PIPELINE_PAGES; page += 1) {
            const response: {data?: ListCodePipelinesResponse} =
                await client.graphql<GraphQLQuery<ListCodePipelinesResponse>>({
                    query: listCodePipelines,
                    variables: {nextToken},
                });

            const connection = response.data?.listCodePipelines;
            if (!connection) {
                return failed(new Error('listCodePipelines returned no data'));
            }

            for (const item of connection.items ?? []) {
                if (item) {
                    names.push(item.name);
                }
            }

            nextToken = connection.nextToken ?? null;
            if (!nextToken) {
                return {complete: true, names};
            }
        }

        // Drained to the cap with a token still outstanding. We have not seen
        // every pipeline, so we cannot say a name is free.
        return {
            complete: false,
            names,
            failureKind: 'TRUNCATED',
            failureReason: `The pipeline listing did not finish within `
                + `${MAX_PIPELINE_PAGES} pages, so a matching pipeline may exist `
                + 'among the results that were not read.',
        };
    } catch (e) {
        return failed(e, graphQLErrorKind(e));
    }
};

// Stacks, by name prefix, in the account this API runs in.
//
// `targetAccount: true` — an application's own infra stack lives in the
// deployment account, not in tools. Omitting it asks tools about a
// shared-svcs stack and gets "does not exist" (proven live on 2026-08-16).
const listStackNames = async (namePrefix: string): Promise<ResourceListing> => {
    try {
        const response = await client.graphql<GraphQLQuery<ListStacksResponse>>({
            query: listStacks,
            variables: {filter: {namePrefix}, targetAccount: true},
        });

        const listing = response.data?.listStacks;
        if (!listing) {
            return failed(new Error('listStacks returned no data'));
        }

        return {
            // ⚠️ Taken from the API, never inferred from items.length.
            complete: listing.complete,
            names: (listing.items ?? [])
                .filter((item): item is { stackName: string; stackStatus: string } => !!item)
                .map((item) => item.stackName),
            failureKind: listing.failureKind,
            failureReason: listing.failureReason,
        };
    } catch (e) {
        return failed(e, graphQLErrorKind(e));
    }
};

// Buckets, by name prefix. No targetAccount by design — a deployment bucket is
// always in the account this API runs in.
const listBucketNames = async (namePrefix: string): Promise<ResourceListing> => {
    try {
        const response = await client.graphql<GraphQLQuery<ListBucketsResponse>>({
            query: listBuckets,
            variables: {filter: {namePrefix}},
        });

        const listing = response.data?.listBuckets;
        if (!listing) {
            return failed(new Error('listBuckets returned no data'));
        }

        return {
            complete: listing.complete,
            names: (listing.items ?? [])
                .filter((item): item is { name: string } => !!item)
                .map((item) => item.name),
            failureKind: listing.failureKind,
            failureReason: listing.failureReason,
        };
    } catch (e) {
        return failed(e, graphQLErrorKind(e));
    }
};

// Decide ONE derived name against the listing that covers it.
//
// ⚠️ THE ORDER OF THESE BRANCHES IS THE SAFETY PROPERTY. `complete` is checked
// FIRST, before `names` is looked at, because an incomplete listing's names are
// not a complete picture — reading them would let a denied listing (zero items)
// fall through to "FREE".
const decide = (derived: DerivedName, listing: ResourceListing): NameAvailability => {
    if (!listing.complete) {
        return {
            ...derived,
            status: 'UNKNOWN',
            failureKind: listing.failureKind,
            failureReason: listing.failureReason,
        };
    }

    // Exact, case-insensitive match. The listings match by PREFIX, so
    // `acme-infra-test` returns for prefix `acme` alongside `acme-2-infra-test`;
    // only the exact name is a collision for this derived name.
    const collision = listing.names.find(
        (name) => name.toLowerCase() === derived.name.toLowerCase(),
    );

    if (collision) {
        return {...derived, status: 'TAKEN', collidesWith: collision};
    }

    return {...derived, status: 'FREE'};
};

// Check every name provisioning will derive from `baseName`.
//
// The four listings run in PARALLEL — they are independent, and a waterfall
// would make a check that runs on every keystroke four round-trips deep.
export const checkNameAvailability = async (
    input: CheckNameAvailabilityInput,
): Promise<NameAvailabilityResult> => {
    const baseName = input.baseName.trim();
    const violations = validateBaseName(baseName);

    // A base name that breaks the derivation rules can never be provisioned, so
    // there is nothing to look up. Returning per-name statuses here would be a
    // fiction about names that will never be created.
    if (violations.length > 0) {
        return {
            baseName,
            violations,
            names: [],
            allFree: false,
            taken: [],
            unknown: [],
        };
    }

    // FNP-1557: the suffix comes from the selected playbook and is folded into
    // both repository names. The listings below still use the UNSUFFIXED base
    // as their prefix, which is deliberate: `flexi-testing` is a prefix of
    // `flexi-testing-dev-app`, so one listing still covers every derived name,
    // and widening it would only fetch more rows to match the same strings
    // against. `decide` matches the full name exactly, never the prefix.
    const derived = deriveNames(baseName, input.targetStages, input.repoNameSuffix);

    const [repositories, pipelines, stacks, buckets] = await Promise.all([
        listRepositoryNames(baseName),
        listPipelineNames(),
        // Only reach for the account-scoped listings when a stage was given;
        // otherwise no stack or bucket name was derived to check.
        derived.some((d) => d.kind === 'CFN_STACK')
            ? listStackNames(baseName)
            : Promise.resolve<ResourceListing>({complete: true, names: []}),
        derived.some((d) => d.kind === 'DEPLOYMENT_BUCKET')
            ? listBucketNames(baseName)
            : Promise.resolve<ResourceListing>({complete: true, names: []}),
    ]);

    const listingFor = (kind: DerivedName['kind']): ResourceListing => {
        switch (kind) {
            case 'SOURCE_REPOSITORY':
            case 'INFRA_REPOSITORY':
                return repositories;
            case 'SOURCE_PIPELINE':
            case 'INFRA_PIPELINE':
                return pipelines;
            case 'CFN_STACK':
                return stacks;
            case 'DEPLOYMENT_BUCKET':
                return buckets;
        }
    };

    const names = derived.map((d) => decide(d, listingFor(d.kind)));

    const taken = names.filter((n) => n.status === 'TAKEN');
    const unknown = names.filter((n) => n.status === 'UNKNOWN');

    return {
        baseName,
        violations,
        names,
        // ⚠️ An UNKNOWN makes this false, exactly as a collision does.
        allFree: names.length > 0 && taken.length === 0 && unknown.length === 0,
        taken,
        unknown,
    };
};
